import {
  HotelCardLink,
  HotelCardLinkProperties,
  hotelCardOnClick
} from "@website/accommodation-ui/hotel-card-new/hotel-card-link";
import { HotelCardThumbnail } from "@website/accommodation-ui/hotel-card-new/hotel-card-thumbnail";
import { HotelCardPropertiesType } from "@website/accommodation-ui/hotel-card-new/hotel-card.types";
import { useIsVisible } from "@website/hooks/is-visible";
import { useViewPort } from "@website/hooks/viewport";
import { addEventToDataLayer } from "@website/tracking/tracking.gtm";
import {
  DATA_LAYER_EVENTS,
  USER_TRACKING_EVENTS
} from "@website/tracking/tracking.types";
import {
  ApplicationNameType,
  addEventToTracking,
  mapProductForUserTracking
} from "@website/tracking/user";
import { Button } from "@website/ui/button";
import { MiniPopover } from "@website/ui/mini-popover";
import { deepCompare } from "@website/utils/deep-compare";
import { useCompatRouter } from "@website/widget-v2/helpers/compat-router.hook";
import omit from "lodash-es/omit";
import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { HotelSnappPayBadge } from "../hotel-snapp-pay";
import { HotelCardAction } from "./hotel-card-action";
import { HotelCardPricing } from "./hotel-card-action/hotel-card-pricing";
import { HotelCardInfo } from "./hotel-card-info";

const appName = process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType;

export const HotelCardDivider: FC<Pick<HotelCardPropertiesType, "size">> = memo(
  ({ size }) => (
    <>
      <div
        className={`block h-px w-full ${size === "small" ? "" : "lg:hidden"}`}
        style={{
          background:
            "repeating-linear-gradient(to right, #EBECF2 0, #EBECF2 4px, transparent 4px, transparent 8px)"
        }}
      />
      {size !== "small" && (
        <div
          className="h-full w-px"
          style={{
            background:
              "repeating-linear-gradient(to bottom, #EBECF2 0, #EBECF2 4px, transparent 4px, transparent 8px)"
          }}
        />
      )}
    </>
  )
);

const Component: FC<HotelCardPropertiesType> = (properties) => {
  const { isMobile } = useViewPort();
  const [clickLoading, setClickLoading] = useState(0);
  const isHomePage =
    process.env.NEXT_PUBLIC_APP_NAME === "homepage" ||
    process.env.NEXT_PUBLIC_APP_NAME === "website";

  const {
    className,
    loading,
    hotelInfo,
    size = "responsive",
    index,
    replace,
    snappPayData,
    isSuggestedCard,
    utm,
    onMouseEnter,
    onMouseLeave,
    onLinkClick,
    targetLink,
    showMap,
    shouldRedirectUsingJs,
    showCarousel = true,
    isLanding,
    homePageSectionName,
    isHotelDomHomePage,
    isVillaHomePage = false,

    isLastMinute
  } = properties;

  const {
    id,
    hotelDetail,
    hotelOffers,
    thumbnail,
    pricing,
    reservationInfo,
    primaryBadge,
    regularBadges,
    advertisementBadge,
    facilities,
    gallery,
    location,
    categories,
    hasSnappProDiscount,
    transportation,
    isInternational
  } = hotelInfo;
  const router = useCompatRouter();
  const { isDesktop } = useViewPort();
  const { reference, inView } = useIsVisible();

  const onClose = useCallback(
    () =>
      router.replace(
        router?.pathname,
        { ...omit(router?.query, "modal") },
        {
          shallow: true,
          scroll: false
        }
      ),
    [router]
  );

  useEffect(() => {
    if (!inView) return;
    if (isHomePage) return;
    addEventToDataLayer(DATA_LAYER_EVENTS.VIEW_ITEM_LIST, {
      items: [
        {
          item_name: hotelInfo?.hotelDetail?.hotelTitle,
          item_id: hotelInfo?.id,
          price: hotelInfo?.pricing?.price,
          item_brand: "IRR",
          item_category: hotelInfo?.hotelDetail?.cityTitle,
          item_category2: hotelInfo?.hotelDetail?.accommodationType,
          item_category3: hotelInfo?.hotelDetail?.stars,
          item_list_name: `هتل های ${hotelInfo?.hotelDetail?.cityTitle}`,
          item_list_id: hotelInfo?.hotelDetail?.cityId,
          index
        }
      ]
    });
    const payload = { position: index, accommodation_id: hotelInfo.id };
    // TODO: add tour to this event when tour feature is ready in user tracking
    if (["villa", "hotel-domestic", "hotel-international"].includes(appName)) {
      addEventToTracking(USER_TRACKING_EVENTS.HOTEL_CARD_VIEWED, {
        date_from: String(router.query.date_from) || "",
        date_to: String(router.query.date_to) || "",
        city: hotelInfo.hotelDetail?.cityTitle,
        city_id: hotelInfo.hotelDetail?.cityId,
        hotel: hotelInfo.hotelDetail?.hotelTitle,
        hotel_id: hotelInfo.id,
        price: hotelInfo.pricing.price,
        price_off: hotelInfo.pricing.priceOff,
        discount_percent: hotelInfo.pricing.discountPercentage,
        is_available: hotelInfo.hotelDetail.isAvailable,
        price_engine_rules: [
          {
            id: hotelInfo?.ruleId ?? 0,
            title: ""
          }
        ],
        product: mapProductForUserTracking(appName),
        ...payload
      });
    }
  }, [hotelInfo, inView, index, router]);

  const linkProperties: HotelCardLinkProperties = useMemo(
    () => ({
      hotelInfo,
      href: hotelInfo?.hotelDetail?.hotelPageUrl?.href,
      pathname: hotelInfo?.hotelDetail?.hotelPageUrl?.pathname,
      query: {
        ...router.query,
        ...(hotelInfo?.hotelDetail?.hotelPageUrl?.query as object)
      },
      setClickLoading,
      index,
      utm,
      replace: !isSuggestedCard && replace,
      target: targetLink,
      title: hotelDetail?.hotelTitle,
      shouldRedirectUsingJs,
      children: "",
      onLinkClick,
      homePageSectionName,
      isHomePage,
      isLastMinute
    }),
    [
      hotelInfo,
      router.query,
      index,
      utm,
      isSuggestedCard,
      replace,
      setClickLoading,
      targetLink,
      hotelDetail,
      onLinkClick,
      homePageSectionName,
      isHomePage,
      isLastMinute
    ]
  );

  return (
    <>
      <div
        className={`bg-on-color-high-emphasis shadow-1 flex flex-col overflow-hidden rounded-2xl p-0 sm:p-3 ${className} ${
          size === "small"
            ? `${appName === "villa" ? "" : "cursor-pointer"} h-full gap-3`
            : "md:flex-row"
        } ${
          size === "xSmall"
            ? "h-[118px] w-[272px] cursor-pointer !flex-row items-center gap-2 !rounded-[8px] !p-2 !shadow-none"
            : ""
        }`}
        onMouseEnter={() => onMouseEnter?.(Number(id))}
        onMouseLeave={() => onMouseLeave?.(Number(id))}
        onClick={
          size === "xSmall" || (size === "small" && appName !== "villa")
            ? hotelCardOnClick({
                query: utm,
                router,
                setClickLoading,
                index,
                hotelInfo,
                onLinkClick,
                homePageSectionName,
                isHomePage,
                isLastMinute
              })
            : undefined
        }
        ref={reference}
      >
        <HotelCardLink {...linkProperties} className="md:flex">
          <HotelCardThumbnail
            gallery={gallery}
            id={id}
            primaryBadge={primaryBadge}
            showCarousel={showCarousel}
            size={size}
            specialOffer={hotelOffers?.specialOffer}
            stars={hotelDetail?.stars}
            thumbnail={thumbnail}
            title={hotelDetail?.hotelTitle}
          />
        </HotelCardLink>

        <div
          className={`flex min-w-0 shrink grow flex-col ${
            size === "xSmall" ? "h-full" : ""
          }`}
        >
          {/*{!["small", "xSmall"].includes(size) && (*/}
          {/*  <HotelCardHeader*/}
          {/*    isAvailable={hotelDetail?.isAvailable}*/}
          {/*    primaryBadge={primaryBadge}*/}
          {/*  />*/}
          {/*)}*/}
          <div
            className={`flex h-full flex-col px-3 ${
              isLanding || size === "small" ? "!pb-3 sm:!pb-0" : "pb-1"
            } sm:p-0 md:gap-3 ${size === "xSmall" ? "!gap-1 !px-0" : ""} ${
              ["small", "xSmall"].includes(size) ? "" : "lg:flex-row"
            }`}
          >
            <HotelCardInfo
              {...hotelDetail}
              isHotelDomHomePage={isHotelDomHomePage}
              linkProperties={linkProperties}
              advertisementBadge={advertisementBadge}
              facilities={facilities}
              id={id}
              isInstant={hotelDetail?.isInstant}
              isSuggestedCard={isSuggestedCard}
              loading={loading}
              location={location}
              offers={hotelOffers?.offers || []}
              pricing={pricing}
              propertyId={hotelDetail?.propertyId}
              regularBadges={regularBadges}
              reservationInfo={reservationInfo}
              showMap={showMap && !isMobile}
              size={size}
              snappPayData={snappPayData}
              categories={categories}
              hasSnappProDiscount={hasSnappProDiscount}
              transportation={transportation}
              isInternational={isInternational}
            />
            {size !== "xSmall" && (
              <div className="md:ps-4 lg:ps-0">
                <HotelCardDivider size={size} />
              </div>
            )}
            {size === "xSmall" ? (
              <HotelCardPricing
                {...pricing}
                size={size}
                hasSnappProDiscount={hasSnappProDiscount}
              />
            ) : (
              <HotelCardAction
                isLanding={isLanding}
                linkProperties={linkProperties}
                clickLoading={clickLoading === id}
                isAvailable={hotelDetail?.isAvailable}
                isFomo={hotelDetail?.isFomo}
                loading={loading}
                pricingInfo={pricing}
                reservationInfo={reservationInfo}
                roomCount={hotelDetail?.roomCount}
                roomTitle={hotelDetail?.roomTitle}
                size={size}
                hasSnappProDiscount={hasSnappProDiscount}
                isHotelDomHomePage={isHotelDomHomePage}
                isVillaHomePage={isVillaHomePage}
              />
            )}
          </div>
        </div>
        {size !== "small" && size !== "xSmall" && (
          <div className="mt-2 block md:hidden">
            <HotelSnappPayBadge
              {...snappPayData}
              loading={loading}
              page="cityPage"
              price={pricing?.priceOff || pricing?.price}
            />
          </div>
        )}
      </div>
      {router?.query?.modal === `badge-info-${id}` && !isDesktop && (
        <div className="block md:hidden">
          <MiniPopover
            onClose={onClose}
            open={router?.query?.modal === `badge-info-${id}`}
            title={primaryBadge?.text}
            className="!z-[100]"
          >
            <div className="flex w-full flex-col gap-4 px-6 pb-6">
              <span className="text-body-2">{primaryBadge?.description}</span>
              <Button
                color="secondary"
                onClick={onClose}
                text="متوجه شدم"
                variant="outline"
              />
            </div>
          </MiniPopover>
        </div>
      )}
    </>
  );
};

export const HotelCard = memo(Component, deepCompare);
